import {
  CompassCardComponent,
  CompassCardComponentProps,
} from 'react-scale/corp/views/DataCompassV2/types';
import useArchieQCResultsAdapterCard from './useArchieQCResultsAdapterCard';
import { Filters } from '@scale/compass-ui/components/filters/Filters';
import { COLUMN_ID_TO_OPTIONS } from 'react-scale/corp/views/DataCompassCardsV2/components/filters/inputFilterOptions';
import { ARCHIE_QC_RESULTS_ADAPTER_COLUMNS, DEFAULT_ARCHIE_QC_RESULTS_LIMIT } from './constants';
import {
  ArchieQCJoinType,
  ArchieQCViewType,
} from '@scale/genai-compass-shared/cards/archie_qc_results_adapter';
import {
  Checkbox,
  Icon,
  Select,
  SegmentedControl,
  Text,
  TextField,
  Tooltip,
} from '@scale/scaleui-radix';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';

const VIEW_DESCRIPTIONS = {
  [ArchieQCViewType.CURRENT_PIPELINE]:
    'This table retrieves the latest task attempt for each task ID in your current pipeline.',
  [ArchieQCViewType.ATTEMPT_BREAKDOWN]:
    'This table breaks down each task attempt by individual error categories, resulting in multiple rows for the same task attempt or task ID if multiple error categories are flagged.',
} as const;

const ArchieQCResultsCardComponent: CompassCardComponent = ({
  index,
}: CompassCardComponentProps) => {
  const {
    filter,
    setFilter,
    resultsLimit,
    setResultsLimit,
    joinType,
    setJoinType,
    viewType,
    setViewType,
  } = useArchieQCResultsAdapterCard(index);
  const [isNoLimit, setIsNoLimit] = useState(false);

  const handleLimitChange = (value: string) => {
    const limit = parseInt(value, 10);
    if (!isNaN(limit) && limit > 0) {
      setResultsLimit(limit);
    }
  };

  const handleNoLimitChange = (checked: boolean) => {
    setIsNoLimit(checked);
    if (checked) {
      setResultsLimit(undefined);
    } else {
      setResultsLimit(DEFAULT_ARCHIE_QC_RESULTS_LIMIT);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-[180px_1fr] gap-y-4 items-center">
        <Text className="text-sm text-neutral-700">View Type:</Text>
        <div className="flex items-center gap-2">
          {/* @ts-ignore */}
          <SegmentedControl.Root
            defaultValue={viewType}
            onValueChange={value => setViewType(value as ArchieQCViewType)}
          >
            <SegmentedControl.Item value={ArchieQCViewType.ATTEMPT_BREAKDOWN}>
              Attempt Breakdown
            </SegmentedControl.Item>
            <SegmentedControl.Item value={ArchieQCViewType.CURRENT_PIPELINE}>
              Current Pipeline
            </SegmentedControl.Item>
          </SegmentedControl.Root>
          <Tooltip content={VIEW_DESCRIPTIONS[viewType as keyof typeof VIEW_DESCRIPTIONS]}>
            <Icon icon={faCircleInfo} className="text-neutral-500" label="View description" />
          </Tooltip>
        </div>

        <Text className="text-sm text-neutral-700">Number of Results:</Text>
        <div className="flex items-center gap-4">
          <TextField.Root
            type={isNoLimit ? 'text' : 'number'}
            placeholder={DEFAULT_ARCHIE_QC_RESULTS_LIMIT.toString()}
            value={
              isNoLimit
                ? '∞'
                : resultsLimit?.toString() ?? DEFAULT_ARCHIE_QC_RESULTS_LIMIT.toString()
            }
            onChange={e => handleLimitChange(e.target.value)}
            className="w-32"
            disabled={isNoLimit}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              checked={isNoLimit}
              onCheckedChange={handleNoLimitChange}
              id="no-limit-results"
            />
            <Text asChild>
              <label htmlFor="no-limit-results" className="text-sm text-neutral-700 cursor-pointer">
                No Limit
              </label>
            </Text>
            <Tooltip content="Fetching results with no limit may be slow">
              <Icon
                icon={faCircleInfo}
                className="text-neutral-500"
                label="Fetching results with no limit may be slow"
              />
            </Tooltip>
          </div>
        </div>

        <Text className="text-sm text-neutral-700">Result Type:</Text>
        <div className="flex items-center gap-4">
          <Select.Root
            value={joinType}
            onValueChange={value => setJoinType(value as ArchieQCJoinType)}
          >
            <Select.Trigger className="w-32" />
            <Select.Content>
              <Select.Item value={ArchieQCJoinType.INNER}>With QC Data Only</Select.Item>
              <Select.Item value={ArchieQCJoinType.LEFT}>All results</Select.Item>
            </Select.Content>
          </Select.Root>
          <Tooltip content="Choose whether to show all Archie results or only those that also have QC data">
            <Icon
              icon={faCircleInfo}
              className="text-neutral-500"
              label="Choose whether to show all Archie results or only those that also have QC data"
            />
          </Tooltip>
        </div>
      </div>
      <Filters
        columns={Object.values(ARCHIE_QC_RESULTS_ADAPTER_COLUMNS)}
        filters={filter}
        setFilters={setFilter}
        columnOptionMap={COLUMN_ID_TO_OPTIONS}
      />
    </div>
  );
};

export default ArchieQCResultsCardComponent;
